/**
 * Keeps Affirm's promotional messaging in step with the price on the page.
 *
 * Affirm reads data-amount once per draw, so a widget left alone would keep
 * quoting the price the page loaded with — wrong on every PDP here, where the
 * total moves as dates, sessions and variants are picked. Step 3 of the
 * quickstart (https://docs.affirm.com/platforms/docs/quickstart) is to re-stamp
 * data-amount and call affirm.ui.refresh().
 *
 * Five different section scripts write that total, each through its own
 * formatMoney, and none of them publish an event. Rather than add an Affirm call
 * to all five — and to whichever is written next — this watches the price
 * element itself, so any of them updating the DOM is enough.
 */
(function () {
  'use strict';

  var REFRESH_DELAY = 120;

  // The live total, in the order each section names it. Deliberately narrow:
  // per-variant and per-date prices carry their own classes, and matching one
  // of those would quote a single session instead of the basket total.
  var PRICE_SELECTOR = [
    '[data-variant-price]',
    '.product-detail__price-sale',
    '.culture-events__price-value',
    '[data-private-classes-total]',
    '[data-total-price]',
  ].join(', ');

  var PRICE_SCOPE_SELECTOR = '[data-price-display], [data-affirm-price-scope]';

  var refreshTimer = null;
  var observed = [];

  /**
   * Cents from a formatted price. "$1,234.50" -> 123450, "FREE" -> 0.
   * Falls back to the element's own cents where a section parks them on
   * data-variant-price, which needs no parsing at all.
   */
  function toCents(text) {
    if (typeof text !== 'string') return null;

    var digits = text.replace(/[^\d.,]/g, '');
    if (!digits) return null;

    // Both separators are in play across locales. Whichever comes last and is
    // followed by exactly two digits is the decimal point; the other groups
    // thousands. No trailing two-digit group means whole units.
    var decimal = /[.,](\d{2})$/.exec(digits);
    var units = decimal ? digits.slice(0, decimal.index) : digits;
    var cents = decimal ? decimal[1] : '00';

    units = units.replace(/[^\d]/g, '');
    if (!units) units = '0';

    var value = parseInt(units, 10) * 100 + parseInt(cents, 10);
    return isNaN(value) ? null : value;
  }

  function amountFrom(priceEl) {
    var raw = priceEl.getAttribute('data-variant-price');
    if (raw !== null && raw !== '' && /^\d+$/.test(raw.trim())) {
      return parseInt(raw.trim(), 10);
    }
    return toCents(priceEl.textContent);
  }

  /** The element holding the total this message belongs to. */
  function findPriceEl(promo) {
    var explicit = promo.getAttribute('data-affirm-watch');
    if (explicit) {
      try {
        var target = document.querySelector(explicit);
        if (target) return target;
      } catch (error) {
        // A selector typo shouldn't take the rest of the page down.
      }
    }

    // The message is rendered directly after its price block, so walk back
    // through siblings before widening to ancestors — a PDP can hold more than
    // one price block (main total, modal total) and the nearest one wins.
    var sibling = promo.previousElementSibling;
    while (sibling) {
      var found = searchScope(sibling);
      if (found) return found;
      sibling = sibling.previousElementSibling;
    }

    var parent = promo.parentElement;
    while (parent && parent !== document.body) {
      var inParent = searchScope(parent);
      if (inParent) return inParent;
      parent = parent.parentElement;
    }

    return null;
  }

  function searchScope(el) {
    var scope = el.matches(PRICE_SCOPE_SELECTOR) ? el : el.querySelector(PRICE_SCOPE_SELECTOR);
    if (scope) {
      var price = scope.matches(PRICE_SELECTOR) ? scope : scope.querySelector(PRICE_SELECTOR);
      if (price) return price;
    }
    return el.matches(PRICE_SELECTOR) ? el : el.querySelector(PRICE_SELECTOR);
  }

  /**
   * Redraw every widget on the page. affirm.ui.refresh only exists once
   * affirm.js has landed, so it goes through ui.ready, which the loader stub
   * queues until then and runs straight away afterwards.
   */
  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      var affirm = window.affirm;
      if (!affirm || !affirm.ui || typeof affirm.ui.ready !== 'function') return;
      affirm.ui.ready(function () {
        if (typeof affirm.ui.refresh === 'function') affirm.ui.refresh();
      });
    }, REFRESH_DELAY);
  }

  function sync(message, priceEl, force) {
    var amount = amountFrom(priceEl);
    if (amount === null) return;

    var next = String(amount);
    if (!force && message.getAttribute('data-amount') === next) return;

    message.setAttribute('data-amount', next);
    scheduleRefresh();
  }

  function watch(promo) {
    if (promo.hasAttribute('data-affirm-bound')) return;

    var message = promo.querySelector('.affirm-as-low-as');
    if (!message) return;

    var priceEl = findPriceEl(promo);
    if (!priceEl) return;

    promo.setAttribute('data-affirm-bound', '');

    // Liquid stamped the amount the page loaded with; this catches a total the
    // section's own script had already rewritten before we got here.
    sync(message, priceEl, false);

    var observer = new MutationObserver(function () {
      sync(message, priceEl, false);
    });
    observer.observe(priceEl, {
      characterData: true,
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-variant-price'],
    });
    observed.push(observer);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var promos = scope.querySelectorAll('[data-affirm-promo]');
    if (!promos.length) return;

    for (var i = 0; i < promos.length; i++) watch(promos[i]);

    // Widgets that Liquid rendered before affirm.js loaded are drawn by the
    // script itself; this covers the ones a section reload brings in later.
    scheduleRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: a re-rendered section brings a fresh, unbound widget with it,
  // and the observers pointed at the markup it replaced are now dead.
  document.addEventListener('shopify:section:load', function (event) {
    for (var i = 0; i < observed.length; i++) observed[i].disconnect();
    observed = [];
    var stale = document.querySelectorAll('[data-affirm-bound]');
    for (var j = 0; j < stale.length; j++) stale[j].removeAttribute('data-affirm-bound');
    init(event.target || document);
    init(document);
  });
})();
